import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, FileSearch } from "lucide-react";
import { api, clearJsonLd, setJsonLd, setSeo, track } from "@/lib/api";
import { SectionRenderer } from "@/components/site/SectionRenderer";
import { CommercialModule, BarrierBadge } from "@/components/site/CommercialModule";
import { GuideCard } from "@/components/site/Blocks";
import { ShortestPath, SidebarProductCard } from "@/components/site/ShortestPath";
import { PriceBands } from "@/components/site/PriceBands";
import { OfferGrid, AffiliateDisclosure } from "@/components/site/OfferGrid";
import { CustomSaunaSection } from "@/components/site/CustomSaunaSection";
import { MerchantCompare } from "@/components/site/MerchantCompare";
import { GuideToolLink } from "@/components/site/RelatedLinks";
import NotFound, { Loading } from "@/pages/NotFound";
import { LoadFailed } from "@/components/site/LoadFailed";
import { SmartImage } from "@/components/site/SmartImage";

const Meta = ({ label, value }) =>
  value ? (
    <div className="py-4 border-b border-line">
      <div className="text-[11px] tracking-[0.16em] uppercase text-[#3F3F3F] font-medium">{label}</div>
      <div className="text-[14.5px] text-charcoal mt-1.5 leading-snug">{value}</div>
    </div>
  ) : null;

export default function GuidePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMissing(false);
    setFailed(false);
    setA(null);
    api.get(`/articles/${slug}`).then((r) => {
      const d = r.data;
      setA(d);
      setSeo({
        title: d.meta_title || d.title,
        description: d.meta_description || d.excerpt,
        image: d.image,
        type: "article",
      });
      setJsonLd("ld-article", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: d.title,
        description: d.meta_description || d.excerpt,
        author: { "@type": "Organization", name: d.author || "Human Home Living Editorial" },
        publisher: { "@type": "Organization", name: "Human Home Living" },
        datePublished: d.published_at,
        dateModified: d.updated_at,
        image: d.image ? [d.image] : undefined,
      });
      setJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
          {
            "@type": "ListItem",
            position: 2,
            name: d.hub_name,
            item: `${window.location.origin}/hub/${d.hub}`,
          },
          { "@type": "ListItem", position: 3, name: d.title },
        ],
      });
      if (d.faqs?.length) {
        setJsonLd("ld-faq", {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: d.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        });
      }
      track({ event_type: "page_view", page: `/guides/${slug}`, article_slug: slug });
    }).catch((err) => {
      // Only a real 404 means the guide is gone. A network failure or a backend restart must not
      // render the noindex 404 view: Googlebot has crawled during exactly that window and excluded
      // a live guide by meta tag.
      if (err.response?.status === 404) setMissing(true);
      else setFailed(true);
    });
    // Schema belongs to this guide only: leaving it behind would describe the next page as an
    // article, which is what a crawler then reports.
    return () => {
      clearJsonLd("ld-article");
      clearJsonLd("ld-breadcrumb");
      clearJsonLd("ld-faq");
    };
  }, [slug]);

  if (failed) return <LoadFailed what="this guide" />;
  if (missing)
    return (
      <NotFound
        title="We couldn't find that guide"
        body="It may have moved. Published decision guides are listed on each category page."
      />
    );
  if (!a) return <Loading label="Loading guide" />;

  // Product-forward guides put the recommendations directly under the quick answer and the cost
  // factors, and keep the rest of the detail below them.
  const pf = a.layout === "product-forward";
  const early = (a.sections || []).filter((s) => s.stage === "before_offers");
  const later = (a.sections || []).filter((s) => s.stage !== "before_offers");

  return (
    <article data-testid={`guide-page-${slug}`}>
      {/* Header */}
      <header className="bg-white border-b border-line px-5 md:px-8 pt-12 md:pt-16 pb-0">
        <div className="max-w-[1080px] mx-auto">
          <nav data-testid="breadcrumb" className="text-[11.5px] font-light text-[#6E6A63] mb-7">
            <Link to="/" className="hover:text-sage">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link to={`/hub/${a.hub}`} className="hover:text-sage">
              {a.hub_name}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-charcoal">{a.product_family}</span>
          </nav>

          <div className="flex flex-wrap items-center gap-2.5 mb-6">
            <BarrierBadge barrier={a.purchase_barrier} />
          </div>

          <h1 className="display text-[38px] md:text-[56px] leading-[1.04] text-charcoal max-w-4xl">
            {a.title}
          </h1>
          <p className="text-[19px] md:text-[26px] font-normal leading-[1.4] text-[#4A4A4A] mt-6 max-w-3xl">
            {a.excerpt}
          </p>

          <div className="flex flex-wrap gap-x-8 gap-y-2 text-[12px] font-light text-[#6E6A63] mt-8 pb-10">
            <span>{a.author}</span>
            {a.last_fact_check && (
              <span>Last fact-check: {new Date(a.last_fact_check).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </header>

      {a.image && (
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 -mt-0">
          <SmartImage
            src={a.image}
            alt={a.image_alt || a.title}
            priority
            sizes="(min-width:1280px) 1248px, 100vw"
            maxWidth={1600}
            className={`w-full h-[280px] md:h-[520px] mt-10 ${
              a.image_fit === "contain-mobile"
                ? "object-contain sm:object-cover bg-[#EDEAE4]"
                : "object-cover"
            }`}
          />
        </div>
      )}

      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 md:py-20 grid lg:grid-cols-[minmax(0,1fr)_290px] gap-12 lg:gap-16">
        <div className="min-w-0 order-1">
          {/* QUICK ANSWER */}
          {a.quick_answer && (
            <section data-testid="quick-answer" className="bg-sage-soft/60 border-l-2 border-sage px-6 md:px-9 py-8">
              <div className="text-[11px] tracking-[0.16em] uppercase text-[#48563F] font-semibold mb-3">
                Quick answer
              </div>
              <p className="text-[17.5px] md:text-[19.5px] font-light leading-[1.6] text-charcoal">
                {a.quick_answer}
              </p>
            </section>
          )}

          <GuideToolLink slug={a.slug} />

          {!pf && <PriceBands tiers={a.price_tiers} scope={a.price_scope} />}

          {pf && (
            <>
              <SectionRenderer sections={early} articleSlug={a.slug} />
              <AffiliateDisclosure
                testId="page-affiliate-disclosure"
                className="mt-14 border-l-2 border-sage/50 pl-4 max-w-3xl"
              />
              {a.offer_showcase && (
                <OfferGrid
                  id="explore-options"
                  category={a.offer_showcase.category}
                  heading={a.offer_showcase.heading}
                  intro={a.offer_showcase.intro}
                  label={a.offer_showcase.label}
                  placement="GUIDE OFFER GRID"
                  articleSlug={a.slug}
                  fillerImage={a.offer_showcase.filler_image}
                />
              )}
              <PriceBands tiers={a.price_tiers} scope={a.price_scope} />
            </>
          )}

          {/* A type comparison is itself the shopping path, so it replaces the generic options
              module rather than sitting alongside it. */}
          {pf ? null : a.merchant_compare ? (
            <>
              <MerchantCompare data={a.merchant_compare} articleSlug={a.slug} />
              <AffiliateDisclosure
                testId="page-affiliate-disclosure"
                className="mt-6 border-l-2 border-sage/50 pl-4 max-w-3xl"
              />
            </>
          ) : (
            <ShortestPath a={a} />
          )}

          {!pf && a.offer_showcase && (
            <AffiliateDisclosure
              testId="page-affiliate-disclosure"
              className="mt-6 border-l-2 border-sage/50 pl-4 max-w-3xl"
            />
          )}

          {/* WHAT THIS GUIDE RESOLVES */}
          {!pf && (
          <div className="bg-white border border-line px-6 py-7 mt-10">
            <div className="text-[11px] tracking-[0.16em] uppercase text-[#48563F] font-semibold mb-3">
              What you'll know
            </div>
            <p data-testid="barrier-resolved" className="text-[17px] md:text-[18px] leading-[1.5] text-[#3F3F3F] max-w-3xl">
              {a.barrier_resolved}
            </p>
          </div>
          )}

          <div id="guide-detail">
            <SectionRenderer
              sections={(() => {
                let laterCta = 0;
                return later.filter((s) => {
                  if (s.type !== "cta") return true;
                  if (s.placement === "EARLY CTA") return false;
                  laterCta += 1;
                  return laterCta === 1;
                });
              })()}
              articleSlug={a.slug}
              shopLabel={a.shop_cta_label}
              offerAnchor={a.offer_showcase ? "#explore-options" : null}
              dedupeCategory={a.primary_offer_category}
            />
          </div>

          {/* FAQ */}
          {a.faqs?.length > 0 && (
            <section data-testid="faq-section" className="mt-16">
              <h2 className="display text-[30px] md:text-[38px] leading-tight text-charcoal mb-7">
                Deeper details
              </h2>
              <div className="border-t border-line">
                {a.faqs.map((f, i) => (
                  <div key={i} className="border-b border-line">
                    <button
                      onClick={() => setOpen(open === i ? null : i)}
                      data-testid={`faq-toggle-${i}`}
                      className="w-full flex items-start justify-between gap-5 py-5 text-left group"
                    >
                      <span className="text-[16.5px] md:text-[17.5px] font-medium text-charcoal group-hover:text-sage-dark transition-colors">
                        {f.q}
                      </span>
                      <ChevronDown
                        size={19}
                        className={`text-[#736F68] shrink-0 mt-0.5 transition-transform duration-300 ${
                          open === i ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {/* Kept in the page source when collapsed, so the answer stays indexable. */}
                    <p
                      data-testid={`faq-answer-${i}`}
                      hidden={open !== i}
                      className="text-[16px] leading-[1.7] text-[#4A4A4A] pb-6 pr-8"
                    >
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* APPROVED MERCHANT OPTIONS — after the editorial content, never above it */}
          {!pf && a.offer_showcase && (
            <OfferGrid
              id="explore-options"
              category={a.offer_showcase.category}
              heading={a.offer_showcase.heading}
              intro={a.offer_showcase.intro}
              label={a.offer_showcase.label}
              placement="GUIDE OFFER GRID"
              articleSlug={a.slug}
              fillerImage={a.offer_showcase.filler_image}
            />
          )}

          {/* CUSTOM PATH — a different kind of option, kept after the ready-made choices */}
          {a.custom_sauna && <CustomSaunaSection data={a.custom_sauna} articleSlug={a.slug} />}

          {/* LATER PRODUCT PATH — only when the body has no contextual CTA of its own */}
          {!a.offer_showcase &&
            a.primary_offer_category &&
            !(a.sections || []).some((s) => s.type === "cta" && s.placement !== "EARLY CTA") && (
            <CommercialModule
              category={a.primary_offer_category}
              fallback={a.alternate_offer_category}
              label={a.shop_cta_label || "Explore Options"}
              headline="Ready to explore options now?"
              note="You've got the detail. Explore the options relevant to this decision whenever you're ready."
              placement="FINAL CTA"
              articleSlug={a.slug}
            />
          )}

          {/* SOURCES */}
          {(a.sources || []).some((s) => s.last_verified) && (
            <section data-testid="sources-section" className="mt-14 border border-line bg-white px-6 md:px-8 py-7">
              <div className="flex items-center gap-2.5 mb-5">
                <FileSearch size={15} className="text-charcoal" />
                <span className="text-[11.5px] tracking-[0.14em] uppercase text-charcoal font-semibold">
                  Sources & verification
                </span>
              </div>
              <div className="divide-y divide-line">
                {a.sources.filter((s) => s.last_verified).map((s, i) => (
                  <div key={i} className="py-4">
                    <div className="text-[14.5px] text-charcoal">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="link-underline hover:text-sage transition-colors"
                        >
                          {s.title}
                        </a>
                      ) : (
                        s.title
                      )}
                    </div>
                    <div className="text-[12.5px] font-light text-[#7A766F] mt-1.5">
                      {s.source_type} · Supports: {s.claim_supported}
                    </div>
                    <div className="text-[12px] font-light text-[#6E6A63] mt-1.5">
                      Verified {s.last_verified}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Policy links. Not a disclosure: the approved disclosure sits above the
              recommendations, so the commission wording is not repeated here. */}
          {(a.offer_showcase || a.merchant_compare) && (
            <p
              data-testid="policy-links"
              className="text-[12.5px] font-light leading-relaxed text-[#6E6A63] mt-10 border-t border-line pt-6"
            >
              Learn more about our{" "}
              <Link to="/affiliate-disclosure" className="text-sage-dark link-underline">
                Affiliate Disclosure
              </Link>{" "}
              and{" "}
              <Link to="/editorial-standards" className="text-sage-dark link-underline">
                Editorial Standards
              </Link>
              .
            </p>
          )}

          {/* DISCLOSURE — suppressed when the approved disclosure already sits above the
              recommendations, so each page carries exactly one. */}
          {!a.offer_showcase && !a.merchant_compare && (
            <p
              data-testid="article-disclosure"
              className="text-[12.5px] font-light leading-relaxed text-[#6E6A63] mt-10 border-t border-line pt-6"
            >
              Affiliate disclosure: some links on Human Home Living may be affiliate links, and we may
              earn a commission at no additional cost to you. That does not affect our conclusions. See
              our{" "}
              <Link to="/affiliate-disclosure" className="text-sage-dark link-underline">
                affiliate disclosure
              </Link>{" "}
              and{" "}
              <Link to="/editorial-standards" className="text-sage-dark link-underline">
                editorial standards
              </Link>
              .
            </p>
          )}
        </div>

        {/* Sidebar */}
        <aside className="order-2 lg:sticky lg:top-[104px] xl:top-[112px] h-fit">
          <SidebarProductCard a={a} />
          <div className="border-t-2 border-charcoal">
            <div className="text-[11px] tracking-[0.16em] uppercase text-[#3F3F3F] font-medium pt-5 pb-1">
              At a glance
            </div>
            <Meta label="The question" value={a.target_question} />
            <Meta label="What to consider" value={a.purchase_barrier} />
            <Meta label="Product family" value={a.product_family} />
          </div>

          {a.tool && (
            <Link
              to={`/tools/${a.tool.slug}`}
              data-testid="sidebar-tool-link"
              className="block mt-8 bg-charcoal text-white px-6 py-7 hover:bg-[#3C3C3C] transition-colors duration-400"
            >
              <span className="text-[11px] tracking-[0.16em] uppercase text-[#B7C6B0] font-semibold">Related tool</span>
              <div className="display text-[22px] leading-snug mt-2.5">{a.tool.name}</div>
              <span className="text-[11.5px] tracking-[0.16em] uppercase text-white/70 mt-4 inline-block">
                Open →
              </span>
            </Link>
          )}
        </aside>
      </div>

      {a.related?.length > 0 && (
        <section className="bg-white border-t border-line px-5 md:px-8 py-16 md:py-20">
          <div className="max-w-[1280px] mx-auto">
            <h2 className="display text-[28px] md:text-[36px] text-charcoal mb-9">Related decisions</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {a.related.map((r, i) => (
                <GuideCard key={r.slug} a={r} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
